import { AccountInfo, Connection, PublicKey } from '@solana/web3.js';
import { Observable, BehaviorSubject } from 'rxjs';
import { dlog } from 'src/dev';
import Env from 'src/environmentVariables/EnvironmentVariables';
import { parseTokenAccountBalance } from 'src/utils/parseTokenAccountData';

const USE_WEBSOCKET_SUBSCRIPTION_REFRESH_INTERVAL = false; // Leaving this disabled for now because we think the bug that caused adding this was a rare ephemeral network issue and it's better not to add this extra load to the RPCs
const WEBSOCKET_SUBSCRIPTION_REFRESH_INTERVAL = 60_000; // 60 seconds

const getTokenAccountBalance = async (
	tokenAccountAddress: PublicKey,
	connection: Connection
): Promise<number> => {
	try {
		const tokenAccountBalance =
			await connection.getTokenAccountBalance(tokenAccountAddress);

		const returnNum = tokenAccountBalance.value.uiAmount
			?.toString()
			?.includes('e')
			? 0
			: tokenAccountBalance.value.uiAmount;

		return returnNum ?? -1;
	} catch (e) {
		return -1;
	}
};

export const createTokenBalanceObservable = (
	tokenAccountAddress: string,
	connection: Connection,
	decimals?: number
): Observable<number | undefined> => {
	const tokenBalanceSubject = new BehaviorSubject<number | undefined>(
		undefined
	);

	const accountChangeListenerRef: { current: number | undefined } = {
		current: undefined,
	};

	const observable = new Observable<number | undefined>((subscriber) => {
		// Subscribe to the BehaviorSubject
		const subscription = tokenBalanceSubject.subscribe(subscriber);

		// Get the initial token balance and push to the subject
		const checkAccountExists = () => {
			return new Promise<boolean>((resolve) =>
				getTokenAccountBalance(new PublicKey(tokenAccountAddress), connection)
					.then((balance) => {
						const accountExists = balance !== -1;
						tokenBalanceSubject.next(accountExists ? balance : 0);
						resolve(accountExists);
					})
					.catch((e) => {
						tokenBalanceSubject.next(0);
						resolve(false);
						dlog(
							`spl_subscription`,
							`caught_error_fetching_initial_balance_for_spl_account`,
							e
						);
					})
			);
		};

		const setUpConnection = () => {
			accountChangeListenerRef.current = connection.onAccountChange(
				new PublicKey(tokenAccountAddress),
				(accountInfo: AccountInfo<Buffer>) => {
					// If decimals is provided, parse accountData directly (no RPC call)
					// Otherwise, fall back to RPC call for backwards compatibility
					if (decimals !== undefined) {
						try {
							const balance = parseTokenAccountBalance(accountInfo, decimals);

							tokenBalanceSubject.next(balance);
						} catch (e) {
							// Fallback to RPC call on parse error
							dlog(
								`spl_subscription`,
								`parse_error_falling_back_to_rpc :: ${tokenAccountAddress}`,
								e
							);
							getTokenAccountBalance(
								new PublicKey(tokenAccountAddress),
								connection
							).then((balance) => {
								tokenBalanceSubject.next(balance === -1 ? 0 : balance);
							});
						}
					} else {
						// Legacy behavior: make RPC call
						getTokenAccountBalance(
							new PublicKey(tokenAccountAddress),
							connection
						).then((balance) => {
							tokenBalanceSubject.next(balance === -1 ? 0 : balance);
						});
					}
				},
				Env.defaultCommitmentLevel
			);
		};

		const teardownConnection = () => {
			if (accountChangeListenerRef.current !== undefined) {
				connection.removeAccountChangeListener(
					accountChangeListenerRef.current
				);
			}
		};

		const refreshConnection = () => {
			dlog(
				`spl_subscription`,
				`refreshing_account_change_listener_for_spl_account :: ${tokenAccountAddress}`
			);
			teardownConnection();
			setUpConnection();
		};

		const initialCheckAccountExistsResultRef: {
			current: boolean | undefined;
		} = {
			current: undefined,
		};

		// Set up the initial connection
		checkAccountExists().then((accountExists) => {
			initialCheckAccountExistsResultRef.current = accountExists;
			if (!accountExists) {
				dlog(
					`spl_subscription`,
					`skipping_account_change_listener_for_non_existent_spl_account `
				);
			} else {
				dlog(
					`spl_subscription`,
					`creating_account_change_listener_for_spl_account :: ${tokenAccountAddress}`
				);
				setUpConnection();
			}
		});

		const refreshIntervalRef: { current: NodeJS.Timeout | undefined } = {
			current: undefined,
		};

		// Refresh the connection based on the interval. Necessary because sometimes the websocket subscription seems to be broken without any real warning
		if (USE_WEBSOCKET_SUBSCRIPTION_REFRESH_INTERVAL) {
			refreshIntervalRef.current = setInterval(() => {
				if (initialCheckAccountExistsResultRef.current) {
					// Only refresh the connection if we know that the account exists
					refreshConnection();
				}
			}, WEBSOCKET_SUBSCRIPTION_REFRESH_INTERVAL) as NodeJS.Timeout;
		}

		// Return the teardown logic
		return () => {
			dlog(
				`spl_subscription`,
				`killing_account_change_listener_for_spl_account :: ${tokenAccountAddress}`
			);

			// Clear the refresh interval
			if (refreshIntervalRef.current !== undefined) {
				clearInterval(refreshIntervalRef.current);
			}

			// Teardown the connection
			teardownConnection();

			// Unsubscribe from the subject
			subscription.unsubscribe();
		};
	});

	return observable;
};
